import React, { useEffect, useState, useContext } from "react";
import FurtherDeductionSchedule from "../../../components/Schedules/Others/FurtherDeductionSchedule";
import { initSchedule } from "../../../utils/data/initData";
import { AuthContext } from "../../../contexts/AuthContext";
import { useErrorDialog } from "../../../contexts/ErrorDialogContext";
import { useSuccessDialog } from "../../../contexts/SuccessDialogContext";
import ComponentLoading from "../../../components/App/ComponentLoading";
import { PL_SCHEDULES } from "../../../utils/data/controlData";
import { handleScheduleDeletion } from "../utils/handleScheduleDeletion";
import { useAccessControl } from "../../../utils/hooks/useAccessControl";

const FurtherDeductionSchedulePage = ({ company, scheduleId }) => {
  const { updatePLSchedule, getScheduleData, deleteScheduleByScheduleType } =
    useContext(AuthContext);
  const [saveTrigger, setSaveTrigger] = useState(false);
  const [schedule, setSchedule] = useState({});
  const [scheduleDetails, setScheduleDetails] = useState({});

  const { canEdit: canEditPLSchedule } = useAccessControl(PL_SCHEDULES);

  const [loading, setLoading] = useState(true);

  const handleError = useErrorDialog();
  const handleSuccess = useSuccessDialog();

  const saveSchedule = async (schedule, scheduleDetails) => {
    if (!company || !scheduleId) {
      handleError("Unexpected error happens. Please try again later.");
      return;
    }

    const updatedSchedule = {
      sch_id: scheduleId,
      scheduleType: "Further Deduction",
      scheduleTitle: schedule.name,
      isExpenseCapped: scheduleDetails.isExpenseCapped,
      isExcessDeductionEnabled:
        Boolean(scheduleDetails.isExpenseCapped) &&
        (scheduleDetails.isExcessDeductionEnabled ??
          Number(scheduleDetails.excessDeductionRate) > 0),
      deductionRate: scheduleDetails.deductionRate,
      expenseCappedAmount: scheduleDetails.expenseCappedAmount,
      expensesRows: scheduleDetails.expensesRows,
      notes: schedule.notes,
      reviewNotes: schedule.reviewNotes,
      totalAdjustment: scheduleDetails.totalAdjustment,
      exchangeRate: scheduleDetails.exchangeRate,
      excessDeductionRate: scheduleDetails.excessDeductionRate,
      excessDeductionDescription: scheduleDetails.excessDeductionDescription,
      excessDeduction: scheduleDetails.excessDeduction,
      totalDeduction: scheduleDetails.totalDeduction,
      doc: scheduleDetails.doc,
    };

    setLoading(true);
    const response = await updatePLSchedule(updatedSchedule);
    setLoading(false);

    if (response) {
      handleSuccess("The schedule has been saved successfully.");
      setSaveTrigger((prev) => !prev);
    } else {
      handleError(
        "There was a problem saving the schedule. Please contact support if the issue persists."
      );
    }
  };

  const deleteSchedule = async () => {
    await handleScheduleDeletion({
      scheduleId,
      deleteFn: deleteScheduleByScheduleType,
      handleSuccess,
      handleError,
      setLoading,
    });
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      if (!company || !scheduleId) {
        handleError(
          "We encountered an issue while fetching the data. Please refresh the page or try again shortly."
        );
        return;
      }
      //API call here
      const response = await getScheduleData(scheduleId, 'Further_Deduction');

      const scheduleData = response.schedule;

      //init
      if (scheduleData) {
        setSchedule(initSchedule(scheduleData, "Further Deductions"));
        setScheduleDetails({
          isExpenseCapped: scheduleData.isExpenseCapped,
          isExcessDeductionEnabled:
            Boolean(scheduleData.isExpenseCapped) &&
            (scheduleData.isExcessDeductionEnabled ??
              Number(scheduleData.excessDeductionRate) > 0),
          expensesRows: scheduleData.expensesRows,
          expenseCappedAmount: scheduleData.expenseCappedAmount,
          deductionRate: scheduleData.deductionRate,
          exchangeRate: scheduleData.exchangeRate,
          excessDeductionRate: scheduleData.excessDeductionRate ?? 0,
          excessDeductionDescription: scheduleData.excessDeductionDescription ?? "",
        });
      }

      setLoading(false);
    };
    fetchData();
  }, [saveTrigger, scheduleId, getScheduleData, company.uen, updatePLSchedule]);

  return (
    <>
      {loading ? (
        <ComponentLoading />
      ) : (
        <FurtherDeductionSchedule
          company={company}
          schedule={schedule}
          scheduleDetails={scheduleDetails}
          saveSchedule={saveSchedule}
          deleteSchedule={deleteSchedule}
          canEditSchedule={canEditPLSchedule}
        />
      )}
    </>
  );
};

export default FurtherDeductionSchedulePage;
